import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

import { providerDeviceService } from '@/services/providers';
import { planQueries } from '@/services/plans/queries';
import { toast } from 'sonner';

interface DeviceManagementProps {
  providerId: number;
  versionId: number;
  regionId?: number;
}

interface DeviceData {
  device_id: number;
  device_name: string;
  device_category: string;
  is_supported: boolean;
  notes?: string | null;
}

interface Plan {
  id: number;
  name: string;
  channel_count?: number;
}

export function DeviceManagement({ 
  providerId, 
  versionId, 
  regionId 
}: DeviceManagementProps) {
  const [appliesTo, setAppliesTo] = useState<'provider' | 'plan' | 'category' | 'package'>('provider');
  const [selectedPlans, setSelectedPlans] = useState<number[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [providerDevices, setProviderDevices] = useState<DeviceData[]>([]);
  const [planDevices, setPlanDevices] = useState<DeviceData[]>([]);
  const [loading, setLoading] = useState(false);
  const [providerTemplateSaved, setProviderTemplateSaved] = useState(false);
  const [planOverridesSaved, setPlanOverridesSaved] = useState<Record<number, boolean>>({});

  // Load existing data
  useEffect(() => {
    loadData();
  }, [providerId, versionId, regionId]);

  // Auto-switch to plan mode when plan overrides exist (after refresh)
  useEffect(() => {
    if (Object.keys(planOverridesSaved).length > 0 && appliesTo === 'provider') {
      setAppliesTo('plan');
    }
  }, [planOverridesSaved, appliesTo]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load all available devices first
      const allDevices = await providerDeviceService.getAllDevices();
      
      // Load existing provider device support
      const deviceSupportData = await providerDeviceService.getProviderDeviceSupport(providerId, versionId, regionId);
      
      // Create a map of supported devices
      const supportMap = new Map();
      if (deviceSupportData && deviceSupportData.length > 0) {
        deviceSupportData.forEach((item: any) => {
          supportMap.set(item.device_id, {
            is_supported: item.is_supported,
            notes: item.notes
          });
        });
        setProviderTemplateSaved(true);
      }
      
      // Format all devices with support status
      const formattedDevices = allDevices.map((device: any) => {
        const support = supportMap.get(device.id) || { is_supported: false, notes: null };
        return {
          device_id: device.id,
          device_name: device.name || 'Unknown Device',
          device_category: device.category || 'Unknown',
          is_supported: support.is_supported,
          notes: support.notes
        };
      });
      
      setProviderDevices(formattedDevices);
      setPlanDevices([...formattedDevices]);

      // Load plans
      const plansData = await planQueries.getPlans(providerId, versionId);
      setPlans(plansData || []);

      // Load existing plan-specific device support if any
      if (plansData && plansData.length > 0) {
        const planIds = plansData.map((plan: any) => plan.id);
        const planOverrides = await providerDeviceService.getPlanOverrides(planIds);
        
        if (planOverrides && planOverrides.length > 0) {
          // Mark plans with overrides as saved
          const savedPlans = [...new Set(planOverrides.map((override: any) => override.plan_id))] as number[];
          
          const savedState: Record<number, boolean> = {};
          savedPlans.forEach((planId: number) => {
            savedState[planId] = true;
          });
          setPlanOverridesSaved(savedState);
          
          // Group device overrides by plan
          const planDevicesMap = new Map();
          planOverrides.forEach((override: any) => {
            if (!planDevicesMap.has(override.plan_id)) {
              planDevicesMap.set(override.plan_id, []);
            }
            planDevicesMap.get(override.plan_id).push({
              device_id: override.device_id,
              device_name: allDevices.find((d: any) => d.id === override.device_id)?.name || 'Unknown Device',
              device_category: allDevices.find((d: any) => d.id === override.device_id)?.category || 'Unknown',
              is_supported: override.is_supported,
              notes: override.notes
            });
          });
          
          // If there are plan overrides, select those plans but keep current mode
          if (savedPlans.length > 0) {
            setSelectedPlans(savedPlans as number[]);
            
            // Set the first plan's device data to show in the form
            const firstPlanId = Array.from(planDevicesMap.keys())[0];
            const firstPlanDevices = planDevicesMap.get(firstPlanId);
            setPlanDevices(firstPlanDevices);
          }
        }
      }

    } catch (error) {
      console.error('Error loading data:', error);
      toast.error("Failed to load existing data", {
        style: { backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }
      });
    } finally {
      setLoading(false);
    }
  };

  const handleProviderTemplateSave = async () => {
    setLoading(true);
    try {
      // Save all device support settings
      for (const device of providerDevices) {
        await providerDeviceService.upsertDeviceSupport({
          provider_id: providerId,
          version_id: versionId,
          region_id: regionId || null,
          device_id: device.device_id,
          is_supported: device.is_supported,
          notes: device.notes,
          entity_type: 'provider',
          entity_id: null
        });
      }
      
      console.log('Provider device support saved successfully');
      setProviderTemplateSaved(true);
      
      // Copy provider data to plan data
      setPlanDevices([...providerDevices]);

      toast.success("Provider template saved successfully", {
        style: { backgroundColor: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' }
      });
    } catch (error) {
      console.error('Error saving provider template:', error);
      
      // Enhanced error handling with different colors
      if (error instanceof Error) {
        if (error.message.includes('duplicate key value violates unique constraint')) {
          toast.success("Device support updated successfully", {
            style: { backgroundColor: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' }
          });
        } else if (error.message.includes('23505')) {
          toast.success("Device support updated successfully", {
            style: { backgroundColor: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' }
          });
        } else {
          toast.error(error.message, {
            style: { backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }
          });
        }
      } else {
        toast.error("Failed to save provider template", {
          style: { backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePlanTemplateSave = async () => {
    if (selectedPlans.length === 0) {
      toast.error("Please select at least one plan", {
        style: { backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }
      });
      return;
    }

    setLoading(true);
    try {
      // Save overrides for all selected plans
      const planNames = selectedPlans.map(id => plans.find(p => p.id === id)?.name).filter(Boolean);
      
      for (const planId of selectedPlans) {
        try {
          // Save device overrides for this plan
          for (const device of planDevices) {
            await providerDeviceService.upsertPlanOverride(planId, device.device_id, {
              is_supported: device.is_supported,
              notes: device.notes
            });
          }
          console.log(`Plan override saved for plan ${planId}`);
          
          // Mark this plan as saved
          setPlanOverridesSaved(prev => ({ ...prev, [planId]: true }));
        } catch (planError) {
          console.error(`Error saving override for plan ${planId}:`, planError);
          throw new Error(`Failed to save override for plan ${planId}: ${planError instanceof Error ? planError.message : 'Unknown error'}`);
        }
      }

      toast.success(`Plan overrides saved for: ${planNames.join(', ')}`, {
        style: { backgroundColor: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' }
      });
    } catch (error) {
      console.error('Error saving plan template:', error);
      
      // Enhanced error handling with different colors
      if (error instanceof Error) {
        if (error.message.includes('duplicate key value violates unique constraint')) {
          toast.success("Plan device support updated successfully", {
            style: { backgroundColor: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' }
          });
        } else if (error.message.includes('23505')) {
          toast.success("Plan device support updated successfully", {
            style: { backgroundColor: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' }
          });
        } else {
          toast.error(error.message, {
            style: { backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }
          });
        }
      } else {
        toast.error("Failed to save plan template", {
          style: { backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const resetPlanToProviderTemplate = () => {
    setPlanDevices([...providerDevices]);
  };

  const updateDeviceSupport = (deviceId: number, isSupported: boolean, isProviderTemplate: boolean = true) => {
    if (isProviderTemplate) {
      setProviderDevices(prev => prev.map(device => 
        device.device_id === deviceId 
          ? { ...device, is_supported: isSupported }
          : device
      ));
    } else {
      setPlanDevices(prev => prev.map(device => 
        device.device_id === deviceId 
          ? { ...device, is_supported: isSupported }
          : device
      ));
    }
  };



  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Device Support Management</h2>
          <p className="text-muted-foreground">Configure device compatibility for this provider.</p>
        </div>
        <Badge variant="outline">
          {appliesTo === 'provider' ? 'Provider Template' : 'Plan Overrides'}
        </Badge>
      </div>

      {/* Applies To Selector */}
      <Card>
        <CardHeader>
          <CardTitle>Applies To</CardTitle>
        </CardHeader>
        <CardContent>
          <Select
            value={appliesTo}
            onValueChange={(value: 'provider' | 'plan' | 'category' | 'package') => setAppliesTo(value)}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="provider">Provider-wide</SelectItem>
              <SelectItem value="plan">Plan-wise</SelectItem>
              <SelectItem value="category">Category-wise</SelectItem>
              <SelectItem value="package">Package-wise</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Provider Template Component (Always shown) */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Template Configuration</CardTitle>
              <p className="text-sm text-muted-foreground">
                These values will apply to all plans by default.
              </p>
            </div>
            {providerTemplateSaved && (
              <Badge variant="secondary" className="bg-green-100 text-green-800">
                ✓ Saved
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {providerDevices.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">
              No device support data available. Please configure device support first.
            </p>
          ) : (
            <div className="space-y-4">
              {providerDevices.map((device) => (
                <div key={device.device_id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <h4 className="font-medium">{device.device_name}</h4>
                    <p className="text-sm text-muted-foreground">{device.device_category}</p>
                  </div>
                  <Switch
                    checked={device.is_supported}
                    onCheckedChange={(checked) => updateDeviceSupport(device.device_id, checked, true)}
                    disabled={false}
                  />
                </div>
              ))}
            </div>
          )}
          <div className="flex justify-end mt-4">
            <Button 
              onClick={handleProviderTemplateSave}
              disabled={loading || providerDevices.length === 0}
              variant={providerTemplateSaved ? "outline" : "default"}
            >
              {loading ? 'Saving...' : providerTemplateSaved ? 'Update Template' : 'Save Template'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Plan Template Component (Shown when plan-wise is selected OR when plan overrides exist) */}
      {(appliesTo === 'plan' || Object.keys(planOverridesSaved).length > 0) && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Plan Template Configuration</CardTitle>
              <div className="flex gap-2">
                {selectedPlans.some(planId => planOverridesSaved[planId]) && (
                  <Badge variant="secondary" className="bg-green-100 text-green-800">
                    ✓ Overrides Saved
                  </Badge>
                )}
                {providerTemplateSaved && !selectedPlans.some(planId => planOverridesSaved[planId]) && (
                  <Badge variant="secondary">Editable</Badge>
                )}
                {!providerTemplateSaved && (
                  <Badge variant="outline">Read-only until provider template saved</Badge>
                )}
              </div>
            </div>
            <p className="text-sm text-muted-foreground">
              {providerTemplateSaved 
                ? 'Edit these values to override the provider template for selected plans.'
                : 'Save the provider template first to enable plan-specific overrides.'
              }
            </p>
            
            {/* Plan Selection Warning */}
            {selectedPlans.length === 0 && (
              <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                  <span className="text-sm text-yellow-800 font-medium">
                    ⚠️ Please select at least one plan to configure plan-specific device support
                  </span>
                </div>
              </div>
            )}
          </CardHeader>
          <CardContent>
            {/* Plan Selection */}
            <div className="mb-6 space-y-2">
              <Label>Select Plans for Override</Label>
              <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto border rounded-md p-3">
                {plans.length === 0 ? (
                  <p className="text-sm text-muted-foreground col-span-2">No plans available</p>
                ) : (
                  plans.map((plan) => (
                    <div key={plan.id} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id={`plan-${plan.id}`}
                        checked={selectedPlans.includes(plan.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedPlans([...selectedPlans, plan.id]);
                          } else {
                            setSelectedPlans(selectedPlans.filter(id => id !== plan.id));
                          }
                        }}
                      />
                      <Label htmlFor={`plan-${plan.id}`} className="text-sm flex items-center gap-2">
                        {plan.name}
                        {planOverridesSaved[plan.id] && (
                          <Badge variant="secondary" className="bg-green-100 text-green-800 text-xs">
                            ✓
                          </Badge>
                        )}
                      </Label>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Plan Device Support Form */}
            {planDevices.length > 0 && (
              <div className="space-y-4">
                {planDevices.map((device) => (
                  <div key={device.device_id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <h4 className="font-medium">{device.device_name}</h4>
                      <p className="text-sm text-muted-foreground">{device.device_category}</p>
                    </div>
                    <Switch
                      checked={device.is_supported}
                      onCheckedChange={(checked) => updateDeviceSupport(device.device_id, checked, false)}
                      disabled={!providerTemplateSaved}
                    />
                  </div>
                ))}
              </div>
            )}
            
            {providerTemplateSaved && (
              <div className="flex justify-between mt-4">
                <Button 
                  variant="outline"
                  onClick={resetPlanToProviderTemplate}
                >
                  Reset to Provider Template
                </Button>
                <Button 
                  onClick={handlePlanTemplateSave}
                  disabled={loading || selectedPlans.length === 0}
                  variant={selectedPlans.some(planId => planOverridesSaved[planId]) ? "outline" : "default"}
                  className="transition-all duration-200 ease-in-out hover:scale-105"
                >
                  {loading ? 'Saving...' : selectedPlans.some(planId => planOverridesSaved[planId]) ? 'Update Plan Overrides' : 'Save Plan Template'}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}